import './fusion.css';
import { tagString, type SeriesRef } from '../dicomweb';
import type { CsImage, CsViewport } from '../globals';
import { addOverlayLines, instanceAt, tagReaderFor } from '../overlay';
import type { ViewerShell } from '../shell';
import type { Viewport } from '../viewport';
import { secondsOfDay, SUV_TEXT, suvFactor, type DicomTime } from './suv';

/**
 * PET/CT fusion (spec section 3, "multi-layer"): a PT series laid over a CT / MR of the same study as
 * a second cornerstone layer (cornerstone-core 2.6.1 layers API), with the " Mapa de color " select
 * (default "pet") and the " Opacidad: N% " slider (default 0.6). The PT layer follows the base stack
 * by image position. PET viewports (fused or not) show the SUV line bottom-left.
 */

export const FUSION_TEXT = {
  colormap: ' Mapa de color ',
  opacity: (percent: number) => ` Opacidad: ${percent}% `,
  menu: 'Fusionar serie',
  menuTitle: 'Superponer una serie PET sobre esta imagen',
  wrongBase: 'La fusión requiere una serie CT o MR en la celda.',
} as const;

/** Colormaps offered, in the IMEXHS order (cornerstone-core ids). */
export const FUSION_COLORMAPS = [
  'hotIron', 'pet', 'hotMetalBlue', 'pet20Step', 'gray', 'jet', 'hsv', 'hot', 'cool', 'spring', 'summer',
  'autumn', 'winter', 'bone', 'copper', 'spectral', 'coolwarm', 'blues',
] as const;
export const DEFAULT_COLORMAP = 'pet';
export const DEFAULT_OPACITY = 0.6;
const FUSION_BASE_MODALITIES = new Set(['CT', 'MR']);
const PET = 'PT';
const OPACITY_STEP = 0.05;
const DISPLAY_OPTIONS = { requestType: 'interaction', priority: -5 } as const;

type Vec3 = [number, number, number];

interface CsLayer {
  layerId: string;
  image?: CsImage;
  viewport?: CsViewport;
  options: { opacity?: number; visible?: boolean; viewport?: Partial<CsViewport> };
  invalid?: boolean;
}

interface LayersApi {
  addLayer(element: HTMLElement, image: CsImage, options?: Record<string, unknown>): string;
  removeLayer(element: HTMLElement, layerId: string): void;
  getLayers(element: HTMLElement): CsLayer[];
  getLayer(element: HTMLElement, layerId: string): CsLayer | undefined;
  setActiveLayer(element: HTMLElement, layerId: string): void;
  setLayerImage(element: HTMLElement, image: CsImage, layerId?: string): void;
}

interface FusionState {
  viewport: Viewport;
  base: SeriesRef;
  pet: SeriesRef;
  petIds: string[];
  /** Distance of each PT slice along the base stack normal. */
  petDepths: number[];
  normal: Vec3;
  baseLayerId: string;
  petLayerId: string;
  colormap: string;
  opacity: number;
  petIndex: number;
  controls: HTMLElement;
  token: number;
  onNewImage: () => void;
}

interface ImagePlane {
  imagePositionPatient?: number[] | null;
  rowCosines?: number[] | null;
  columnCosines?: number[] | null;
}

const states = new Map<Viewport, FusionState>();
let shellRef: ViewerShell | null = null;

function cs(): LayersApi & typeof window.cornerstone {
  return window.cornerstone as LayersApi & typeof window.cornerstone;
}

function plane(imageId: string): ImagePlane | undefined {
  return window.cornerstone.metaData.get('imagePlaneModule', imageId) as ImagePlane | undefined;
}

function vec(values: number[] | null | undefined): Vec3 | null {
  return values && values.length >= 3 ? [Number(values[0]), Number(values[1]), Number(values[2])] : null;
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function depthOf(imageId: string, normal: Vec3): number {
  const position = vec(plane(imageId)?.imagePositionPatient);
  return position ? position[0] * normal[0] + position[1] * normal[1] + position[2] * normal[2] : NaN;
}

function nearestIndex(depths: number[], depth: number): number {
  let best = 0;
  let bestDistance = Infinity;
  depths.forEach((d, i) => {
    const distance = Math.abs(d - depth);
    if (distance < bestDistance) {
      best = i;
      bestDistance = distance;
    }
  });
  return best;
}

/** True when `series` can be fused onto what `viewport` shows (PT over a CT / MR of the same study). */
export function canFuse(viewport: Viewport | undefined, series: SeriesRef | undefined): boolean {
  const base = viewport?.currentStack?.series;
  if (!base || !series || !viewport?.currentImage()) return false;
  return series.modality === PET && FUSION_BASE_MODALITIES.has(base.modality) && base.studyInstanceUID === series.studyInstanceUID
    && base.seriesInstanceUID !== series.seriesInstanceUID;
}

/** PT series of the study shown in `viewport` ("Fusionar serie" submenu). */
export function fusionCandidates(shell: ViewerShell, viewport: Viewport | undefined): SeriesRef[] {
  if (!viewport?.currentStack) return [];
  const study = shell.studies().find((s) => s.studyInstanceUID === viewport.currentStack?.series.studyInstanceUID);
  return (study?.series ?? []).filter((series) => canFuse(viewport, series));
}

export function isFused(viewport: Viewport | undefined): boolean {
  return viewport !== undefined && states.has(viewport);
}

export function fusedSeries(viewport: Viewport | undefined): SeriesRef | undefined {
  return viewport ? states.get(viewport)?.pet : undefined;
}

/** The PET layer on screen in a fused cell (a fusion key image keeps it as its `back_layer`). */
export function fusionLayerInfo(viewport: Viewport | undefined): { series: SeriesRef; index: number; colormap: string; opacity: number } | undefined {
  const state = viewport ? states.get(viewport) : undefined;
  return state ? { series: state.pet, index: state.petIndex, colormap: state.colormap, opacity: state.opacity } : undefined;
}

/** Depth of every PT slice; when the metadata has no geometry yet (parity mode), the images are loaded first. */
async function petDepths(petIds: string[], normal: Vec3): Promise<number[]> {
  let depths = petIds.map((id) => depthOf(id, normal));
  if (depths.some((d) => !Number.isFinite(d))) {
    await Promise.all(petIds.map((id) => window.cornerstone.loadAndCacheImage(id, DISPLAY_OPTIONS)));
    depths = petIds.map((id) => depthOf(id, normal));
  }
  return depths;
}

function baseNormal(imageId: string): Vec3 | null {
  const p = plane(imageId);
  const row = vec(p?.rowCosines);
  const column = vec(p?.columnCosines);
  return row && column ? cross(row, column) : null;
}

/** Shows the PT slice nearest (by image position) to the base image on screen. */
async function followBase(state: FusionState): Promise<void> {
  const image = state.viewport.currentImage();
  if (!image) return;
  const index = nearestIndex(state.petDepths, depthOf(image.imageId, state.normal));
  if (index === state.petIndex) return;
  const token = ++state.token;
  const petImage = await window.cornerstone.loadAndCacheImage(state.petIds[index], DISPLAY_OPTIONS);
  if (token !== state.token || states.get(state.viewport) !== state) return;
  state.petIndex = index;
  cs().setLayerImage(state.viewport.element, petImage, state.petLayerId);
  state.viewport.container.dataset.fusionIndex = String(index);
  window.cornerstone.updateImage(state.viewport.element, true);
}

function petLayer(state: FusionState): CsLayer | undefined {
  return cs().getLayer(state.viewport.element, state.petLayerId);
}

export function setFusionColormap(viewport: Viewport, colormap: string): void {
  const state = states.get(viewport);
  const layer = state && petLayer(state);
  if (!state || !layer?.viewport) return;
  state.colormap = colormap;
  layer.viewport.colormap = colormap;
  layer.invalid = true;
  const select = state.controls.querySelector<HTMLSelectElement>('select');
  if (select && select.value !== colormap) select.value = colormap;
  window.cornerstone.updateImage(viewport.element, true);
}

export function setFusionOpacity(viewport: Viewport, opacity: number): void {
  const state = states.get(viewport);
  const layer = state && petLayer(state);
  if (!state || !layer) return;
  state.opacity = Math.min(1, Math.max(0, opacity));
  // cornerstone skips a layer whose opacity is exactly 0 and treats 0 as "unset" (full opacity) when drawing.
  layer.options.opacity = state.opacity;
  layer.options.visible = state.opacity > 0;
  const percent = Math.round(state.opacity * 100);
  const label = state.controls.querySelector<HTMLElement>('.fusion-opacity-label');
  if (label) label.textContent = FUSION_TEXT.opacity(percent);
  const slider = state.controls.querySelector<HTMLInputElement>('input[type="range"]');
  if (slider && Number(slider.value) !== state.opacity) slider.value = String(state.opacity);
  window.cornerstone.updateImage(viewport.element, true);
}

function buildControls(viewport: Viewport, state: Pick<FusionState, 'colormap' | 'opacity'>): HTMLElement {
  const box = document.createElement('div');
  box.className = 'fusion-controls';
  box.addEventListener('mousedown', (event) => event.stopPropagation());
  box.addEventListener('wheel', (event) => event.stopPropagation(), { passive: true });
  const colormapLabel = document.createElement('label');
  colormapLabel.className = 'fusion-field';
  const colormapText = document.createElement('span');
  colormapText.textContent = FUSION_TEXT.colormap;
  const select = document.createElement('select');
  select.className = 'fusion-colormap';
  const names = new Map(window.cornerstone.colors.getColormapsList().map((c) => [c.id, c.name]));
  for (const id of FUSION_COLORMAPS) {
    if (!names.has(id)) continue;
    const option = document.createElement('option');
    option.value = id;
    option.textContent = names.get(id) ?? id;
    select.appendChild(option);
  }
  select.value = state.colormap;
  select.addEventListener('change', () => setFusionColormap(viewport, select.value));
  colormapLabel.append(colormapText, select);
  const opacityLabel = document.createElement('label');
  opacityLabel.className = 'fusion-field';
  const opacityText = document.createElement('span');
  opacityText.className = 'fusion-opacity-label';
  opacityText.textContent = FUSION_TEXT.opacity(Math.round(state.opacity * 100));
  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '1';
  slider.step = String(OPACITY_STEP);
  slider.value = String(state.opacity);
  slider.className = 'fusion-opacity';
  slider.addEventListener('input', () => setFusionOpacity(viewport, Number(slider.value)));
  opacityLabel.append(opacityText, slider);
  box.append(colormapLabel, opacityLabel);
  return box;
}

/** Removes the PT layer (and the base layer wrapper); the viewport goes back to a plain stack. */
export function unfuse(viewport: Viewport): void {
  const state = states.get(viewport);
  if (!state) return;
  states.delete(viewport);
  state.token++;
  viewport.element.removeEventListener(window.cornerstone.EVENTS.NEW_IMAGE, state.onNewImage);
  state.controls.remove();
  delete viewport.container.dataset.fusion;
  delete viewport.container.dataset.fusionIndex;
  if (!viewport.element.isConnected) return;
  try {
    const api = cs();
    api.removeLayer(viewport.element, state.petLayerId);
    api.removeLayer(viewport.element, state.baseLayerId);
    window.cornerstone.updateImage(viewport.element, true);
  } catch (error) {
    console.warn('[viewer] fusion teardown', error);
  }
}

/**
 * Lays `pet` over the stack in `viewport`: the base image becomes layer 0 (keeping its W/L, zoom and pan),
 * the nearest PT slice layer 1 with the "pet" colormap at 60 % opacity.
 */
export async function fuse(shell: ViewerShell, viewport: Viewport, pet: SeriesRef): Promise<boolean> {
  if (!canFuse(viewport, pet)) {
    shell.toast(FUSION_TEXT.wrongBase, 'warning');
    return false;
  }
  unfuse(viewport);
  const base = viewport.currentStack?.series as SeriesRef;
  const baseImage = viewport.currentImage() as CsImage;
  const normal = baseNormal(baseImage.imageId) ?? [0, 0, 1];
  const petIds = shell.imageIdsFor(pet);
  const depths = await petDepths(petIds, normal);
  const index = nearestIndex(depths, depthOf(baseImage.imageId, normal));
  const petImage = await window.cornerstone.loadAndCacheImage(petIds[index], DISPLAY_OPTIONS);
  // The cell may have moved on while the PT slices loaded.
  if (viewport.currentStack?.series !== base || !viewport.currentImage()) return false;
  const api = cs();
  const element = viewport.element;
  const current = window.cornerstone.getViewport(element);
  const baseLayerId = api.addLayer(element, viewport.currentImage() as CsImage, { viewport: current ? { ...current } : undefined });
  const petLayerId = api.addLayer(element, petImage, { opacity: DEFAULT_OPACITY, visible: true, viewport: { colormap: DEFAULT_COLORMAP } });
  api.setActiveLayer(element, baseLayerId);
  const controls = buildControls(viewport, { colormap: DEFAULT_COLORMAP, opacity: DEFAULT_OPACITY });
  const state: FusionState = {
    viewport, base, pet, petIds, petDepths: depths, normal, baseLayerId, petLayerId,
    colormap: DEFAULT_COLORMAP, opacity: DEFAULT_OPACITY, petIndex: index, controls, token: 0,
    onNewImage: () => void followBase(state),
  };
  states.set(viewport, state);
  element.addEventListener(window.cornerstone.EVENTS.NEW_IMAGE, state.onNewImage);
  viewport.container.appendChild(controls);
  viewport.container.dataset.fusion = pet.seriesInstanceUID;
  viewport.container.dataset.fusionIndex = String(index);
  window.cornerstone.updateImage(element, true);
  return true;
}

// --- SUV line ---------------------------------------------------------------------------------

interface PetIsotopeModule {
  radiopharmaceuticalInfo?: { radiopharmaceuticalStartTime?: DicomTime; radionuclideTotalDose?: number; radionuclideHalfLife?: number };
}

/** Units of a PT image, from its WADO-RS metadata or (parity) the viewport's tag reader. */
function petUnits(series: SeriesRef, imageIds: string[], imageId: string, viewport: Viewport): string {
  const instance = instanceAt(series, Math.max(0, imageIds.indexOf(imageId)));
  const fromJson = tagString(instance?.metadata, '00541001');
  if (fromJson) return fromJson;
  return viewport.currentStack?.series === series ? tagReaderFor(viewport)?.text('00541001') ?? '' : '';
}

function acquisitionSeconds(series: SeriesRef, imageIds: string[], imageId: string): number {
  const seriesModule = window.cornerstone.metaData.get('generalSeriesModule', imageId) as { seriesTime?: DicomTime } | undefined;
  const fromModule = secondsOfDay(seriesModule?.seriesTime);
  if (Number.isFinite(fromModule)) return fromModule;
  const instance = instanceAt(series, Math.max(0, imageIds.indexOf(imageId)));
  return secondsOfDay(tagString(instance?.metadata, '00080031') || tagString(instance?.metadata, '00080032'));
}

/** SUVbw multiplier (Bq/ml → SUV) of one PT image, or null when it cannot be computed. */
export function suvFactorFor(viewport: Viewport, series: SeriesRef, imageIds: string[], imageId: string): number | null {
  const { metaData } = window.cornerstone;
  const isotope = metaData.get('petIsotopeModule', imageId) as PetIsotopeModule | undefined;
  const patient = metaData.get('patientStudyModule', imageId) as { patientWeight?: number } | undefined;
  const info = isotope?.radiopharmaceuticalInfo;
  return suvFactor({
    units: petUnits(series, imageIds, imageId, viewport),
    weight: Number(patient?.patientWeight),
    totalDose: Number(info?.radionuclideTotalDose),
    halfLife: Number(info?.radionuclideHalfLife),
    injectionTime: secondsOfDay(info?.radiopharmaceuticalStartTime),
    seriesTime: acquisitionSeconds(series, imageIds, imageId),
  });
}

/** The PT image a viewport shows: its own stack image, or the fused PT layer's. */
function petOnScreen(viewport: Viewport): { series: SeriesRef; imageIds: string[]; imageId: string } | undefined {
  const state = states.get(viewport);
  if (state) return { series: state.pet, imageIds: state.petIds, imageId: state.petIds[state.petIndex] };
  const stack = viewport.currentStack;
  const image = viewport.currentImage();
  if (!stack || !image || stack.series.modality !== PET) return undefined;
  return { series: stack.series, imageIds: stack.imageIds, imageId: image.imageId };
}

/** SUV factor for the PT on screen in `viewport` (null: not computable, undefined: no PT). */
export function viewportSuvFactor(viewport: Viewport): number | null | undefined {
  const pet = petOnScreen(viewport);
  return pet ? suvFactorFor(viewport, pet.series, pet.imageIds, pet.imageId) : undefined;
}

function suvLines(viewport: Viewport): string[] {
  const factor = viewportSuvFactor(viewport);
  if (factor === undefined) return [];
  viewport.container.dataset.suvFactor = factor === null ? '' : String(factor);
  return [factor === null ? SUV_TEXT.unavailable : SUV_TEXT.formula];
}

// --- Entry points -----------------------------------------------------------------------------

/**
 * Dropping a PT tile on a cell showing a CT / MR of the same study fuses it instead of replacing
 * the stack. Runs in the capture phase so the shell's replace-on-drop handler never sees it.
 */
function installDrop(shell: ViewerShell): void {
  shell.viewports().forEach((viewport) => {
    viewport.container.addEventListener('drop', (event) => {
      const uid = event.dataTransfer?.getData('text/series-uid');
      const pet = shell.studies().flatMap((s) => s.series).find((s) => s.seriesInstanceUID === uid);
      if (!canFuse(viewport, pet)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      shell.activate(shell.viewports().indexOf(viewport));
      void fuse(shell, viewport, pet as SeriesRef);
    }, true);
  });
}

/** Fuses PT series `seriesUID` onto the active viewport ("Fusionar serie" menu entry). */
export async function fuseActive(seriesUID: string): Promise<void> {
  const shell = shellRef;
  const viewport = shell?.activeViewport();
  const pet = shell?.studies().flatMap((s) => s.series).find((s) => s.seriesInstanceUID === seriesUID);
  if (!shell || !viewport || !pet) return;
  await fuse(shell, viewport, pet);
}

export function install(shell: ViewerShell): void {
  shellRef = shell;
  addOverlayLines('bottomLeft', suvLines);
  shell.on('layout-changed', () => {
    states.clear();
    installDrop(shell);
  });
  shell.on('series-shown', ({ viewport, series }) => {
    if (states.get(viewport)?.base !== series) unfuse(viewport);
  });
}
